import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { toWorkbookSummary } from "@/features/workbook/mock-data";
import { useWorkbookStore, type WorkbookEvent } from "@/stores/workbook";

const EMPTY_EVENTS: WorkbookEvent[] = [];

export function WorkbookDetailPage() {
  const { workbookId } = useParams();
  const load = useWorkbookStore((state) => state.load);
  const loadEvents = useWorkbookStore((state) => state.loadEvents);
  const events = useWorkbookStore((state) =>
    workbookId ? state.getEvents(workbookId) : EMPTY_EVENTS,
  );
  const record = useWorkbookStore((state) =>
    workbookId
      ? (state.records.find((item) => item.id === workbookId) ?? null)
      : null,
  );

  const workbook = useMemo(
    () => (record ? toWorkbookSummary(record) : null),
    [record],
  );
  const activeSnapshot = useMemo(
    () =>
      record
        ? (record.snapshots.find(
            (item) => item.id === record.activeSnapshotId,
          ) ??
          record.snapshots[record.snapshots.length - 1] ??
          null)
        : null,
    [record],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!workbookId) {
      return;
    }
    void loadEvents(workbookId);
  }, [loadEvents, workbookId]);

  if (!workbook || !record) {
    return (
      <div className="flex flex-col gap-6">
        <Card>
          <CardContent>
            <CardTitle>Workbook not found</CardTitle>
            <CardSub>
              This workbook may have been removed or the URL is invalid.
            </CardSub>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            {workbook.workbookName}
          </h1>
          <p className="mt-1.5 text-sm text-fg-dim">{workbook.propertyName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="info" size="md">
            {workbook.accessLevel}
          </Badge>
          <Button asChild size="sm">
            <Link to="edit">Edit workbook</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <CardTitle>Status</CardTitle>
            <CardSub>{workbook.status}</CardSub>
          </div>
          <div>
            <CardTitle>Author</CardTitle>
            <CardSub>{workbook.author}</CardSub>
          </div>
          <div>
            <CardTitle>Last updated</CardTitle>
            <CardSub>{workbook.updatedAt}</CardSub>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <CardTitle>Workbook summary</CardTitle>
          <CardSub className="mt-2">{workbook.description}</CardSub>
        </CardContent>
      </Card>

      {activeSnapshot ? (
        <Card>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <CardTitle>Workbook sections (active snapshot)</CardTitle>
              <CardSub>
                Legacy workbook domains persisted through the snapshot history.
              </CardSub>
            </div>
            <div>
              <CardTitle>Hotel Description</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.hotelDescription || "-"}
              </CardSub>
            </div>
            <div>
              <CardTitle>ISP Metadata</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.isp.metadata.provider || "-"}
              </CardSub>
            </div>
            <div>
              <CardTitle>ISP General Details</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.isp.general.sla || "-"}
              </CardSub>
            </div>
            <div>
              <CardTitle>ISP Network Details</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.isp.network.wanCidr || "-"}
              </CardSub>
            </div>
            <div>
              <CardTitle>ISP Router Details</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.isp.router.model || "-"}
              </CardSub>
            </div>
            <div>
              <CardTitle>Equipment Rows</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.equipmentList.length}
              </CardSub>
            </div>
            <div>
              <CardTitle>Switch Port Rows</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.switchPortMap.length}
              </CardSub>
            </div>
            <div>
              <CardTitle>VLAN Legend Rows</CardTitle>
              <CardSub className="mt-1">
                {activeSnapshot.sections.vlanLegend.length}
              </CardSub>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div>
            <CardTitle>Snapshot timeline</CardTitle>
            <CardSub>
              Event-sourced history of workbook metadata revisions.
            </CardSub>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <caption className="sr-only">Workbook snapshot timeline</caption>
              <thead className="bg-surface-2 text-left text-xs uppercase tracking-[0.12em] text-fg-mute">
                <tr>
                  <th className="px-4 py-3 font-semibold">Snapshot</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold">By</th>
                  <th className="px-4 py-3 font-semibold">Reason</th>
                  <th className="px-4 py-3 font-semibold text-right">State</th>
                </tr>
              </thead>
              <tbody>
                {[...record.snapshots].reverse().map((snapshot) => (
                  <tr key={snapshot.id} className="border-t border-line">
                    <td className="px-4 py-3 font-mono text-xs text-fg-dim">
                      {snapshot.id}
                    </td>
                    <td className="px-4 py-3 text-fg">
                      {snapshot.createdAt.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3 text-fg">{snapshot.createdBy}</td>
                    <td className="px-4 py-3 text-fg">{snapshot.reason}</td>
                    <td className="px-4 py-3 text-right">
                      {snapshot.id === record.activeSnapshotId ? (
                        <Badge variant="ok" size="sm">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">
                          Historical
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div>
            <CardTitle>Event timeline</CardTitle>
            <CardSub>
              Append-only event history used to rebuild workbook state.
            </CardSub>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <caption className="sr-only">Workbook event timeline</caption>
              <thead className="bg-surface-2 text-left text-xs uppercase tracking-[0.12em] text-fg-mute">
                <tr>
                  <th className="px-4 py-3 font-semibold">Event</th>
                  <th className="px-4 py-3 font-semibold">Occurred</th>
                  <th className="px-4 py-3 font-semibold">Actor</th>
                  <th className="px-4 py-3 font-semibold">Snapshot</th>
                  <th className="px-4 py-3 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {events.length > 0 ? (
                  events.map((event) => (
                    <tr key={event.id} className="border-t border-line">
                      <td className="px-4 py-3 text-fg">{event.eventType}</td>
                      <td className="px-4 py-3 text-fg-dim">
                        {event.occurredAt.slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-4 py-3 text-fg">{event.actor}</td>
                      <td className="px-4 py-3 font-mono text-xs text-fg-dim">
                        {event.snapshotId || "-"}
                      </td>
                      <td className="px-4 py-3 text-fg-dim">
                        {typeof event.payload.reason === "string"
                          ? event.payload.reason
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-fg-dim"
                    >
                      No events available yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
